/**
 * Browser-local key vault.
 * Keys live in localStorage under tsm_vault_<provider>.
 * This is the "no-account" BYOK tier. When Google profile sync is enabled,
 * users can opt in to copying these keys into the encrypted server vault.
 */

import {
  VAULT_PROVIDERS,
  type VaultEntry,
  type VaultProvider,
  type VaultSnapshot,
} from "@shared/profileVault";

const PREFIX = "tsm_vault_";
const RETIRED_VAULT_KEYS = [`${PREFIX}zo`] as const;

export type { VaultEntry, VaultProvider, VaultSnapshot } from "@shared/profileVault";

interface StoredVaultEntry extends VaultEntry {
  provider: VaultProvider;
}

function key(provider: VaultProvider): string {
  return `${PREFIX}${provider}`;
}

export function purgeRetiredBrowserVaultEntries(
  storage?: Pick<Storage, "removeItem"> | null,
): void {
  let target = storage;
  if (target === undefined) {
    try {
      target = typeof window === "undefined" ? null : window.localStorage;
    } catch {
      return;
    }
  }
  if (!target) return;
  for (const retiredKey of RETIRED_VAULT_KEYS) {
    try {
      target.removeItem(retiredKey);
    } catch {
      // Storage can be unavailable in privacy modes. Active vault reads remain fail-safe.
    }
  }
}

purgeRetiredBrowserVaultEntries();

export function normalizeVaultSecret(value: string | null | undefined): string | null {
  const cleaned = (value ?? "").replace(/[\u0000-\u001F\u007F]/g, "").trim();
  return cleaned || null;
}

function normalizeVaultSavedAt(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : new Date(timestamp).toISOString();
}

export function setVaultKey(provider: VaultProvider, value: string): void {
  const cleaned = normalizeVaultSecret(value);
  if (!cleaned) {
    removeVaultKey(provider);
    return;
  }
  const entry: StoredVaultEntry = { provider, value: cleaned, savedAt: new Date().toISOString() };
  localStorage.setItem(key(provider), JSON.stringify(entry));
}

export function getVaultEntry(provider: VaultProvider): VaultEntry | null {
  try {
    const raw = localStorage.getItem(key(provider));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredVaultEntry>;
    const value = normalizeVaultSecret(parsed.value);
    if (!value) return null;
    return {
      value,
      savedAt: normalizeVaultSavedAt(parsed.savedAt),
    };
  } catch {
    return null;
  }
}

export function getVaultKey(provider: VaultProvider): string | null {
  return getVaultEntry(provider)?.value ?? null;
}

export function hasVaultKey(provider: VaultProvider): boolean {
  return getVaultKey(provider) !== null;
}

export function removeVaultKey(provider: VaultProvider): void {
  localStorage.removeItem(key(provider));
}

export function listVaultKeys(): VaultProvider[] {
  return VAULT_PROVIDERS.filter((provider) => getVaultEntry(provider) !== null);
}

export function readVaultSnapshot(): VaultSnapshot {
  const snapshot: VaultSnapshot = {};
  for (const provider of VAULT_PROVIDERS) {
    const entry = getVaultEntry(provider);
    if (entry) snapshot[provider] = entry;
  }
  return snapshot;
}

export function clearVault(): void {
  VAULT_PROVIDERS.forEach(removeVaultKey);
}

export function useKeyVault() {
  return {
    setVaultKey,
    getVaultKey,
    getVaultEntry,
    hasVaultKey,
    removeVaultKey,
    listVaultKeys,
    readVaultSnapshot,
    clearVault,
  };
}
